import * as SystemConstants from "./SystemConstants";
import { produce } from "immer";
import moment from "moment-timezone";
import { DATE_TIME_FORMAT } from "Common/constants/commonConstants";
// Object.assign is not yet fully supported in all browsers, so we fallback to a polyfill
const assign = Object.assign || require("object.assign");

const initialState = {};

export default function SystemReducer(state = initialState, action) {
  switch (action.type) {
    case SystemConstants.SYSTEM_COMPONENT_DEVICE_STATE_ADD: {
      if (state[action.payload]) {
        return state;
      }

      const dateUtcNow = moment.utc();
      const newState = produce(state, (draft) => {
        draft[action.payload] = {
          ...draft[action.payload],
          fetchDeviceComponentListStatus: SystemConstants.SUCCESS_STATUS,
          fetchDeviceComponentListErrorMessage: "",
          deviceComponentList: [],
          selectedFilterVals: {
            component: [],
            status: [],
            serialNumber: [],
            swVersion: [],
          },
          filterOptions: {
            component: [],
            status: [],
            serialNumber: [],
            swVersion: [],
          },
          selectedComponent: null,
          fetchComponentMetricsStatus: SystemConstants.SUCCESS_STATUS,
          fetchComponentMetricsErrorMessage: "",
          componentMetrics: [],
          selectedComponentMetric: [],
          selectedDateRange: [
            moment(dateUtcNow).subtract(1, "days").format(DATE_TIME_FORMAT),
            moment(dateUtcNow).format(DATE_TIME_FORMAT),
          ],
          fetchMeasurementValsStatus: SystemConstants.SUCCESS_STATUS,
          fetchMeasurementValsStatusErrorMessage: "",
          measurementVals: [],
        };
      });
      return assign({}, state, newState);
    }
    case SystemConstants.SYSTEM_COMPONENT_DEVICE_STATE_REMOVE: {
      const newState = produce(state, (draft) => {
        if (draft[action.payload]) {
          delete draft[action.payload];
        }
      });
      return assign({}, newState);
    }
    case SystemConstants.DEVICE_COMPONENT_LIST_FETCH_REQUEST: {
      const dateUtcNow = moment.utc();
      const newState = produce(state, (draft) => {
        draft[action.payload] = {
          ...draft[action.payload],
          fetchDeviceComponentListStatus: SystemConstants.FETCHING_STATUS,
          fetchDeviceComponentListErrorMessage: "",
          deviceComponentList: [],
          selectedFilterVals: {
            component: [],
            status: [],
            serialNumber: [],
            swVersion: [],
          },
          filterOptions: {
            component: [],
            status: [],
            serialNumber: [],
            swVersion: [],
          },
          selectedComponent: null,
          fetchComponentMetricsStatus: SystemConstants.SUCCESS_STATUS,
          fetchComponentMetricsErrorMessage: "",
          componentMetrics: [],
          selectedComponentMetric: [],
          selectedDateRange: [
            moment(dateUtcNow).subtract(1, "days").format(DATE_TIME_FORMAT),
            moment(dateUtcNow).format(DATE_TIME_FORMAT),
          ],
          fetchMeasurementValsStatus: SystemConstants.SUCCESS_STATUS,
          fetchMeasurementValsStatusErrorMessage: "",
          measurementVals: [],
        };
      });
      return assign({}, state, newState);
    }
    case SystemConstants.DEVICE_COMPONENT_LIST_FETCH_SUCCESS: {
      const filterOptions = {
        component: action.payload.result
          .map((el) => el.component)
          .filter((el, index, arr) => arr.indexOf(el) === index)
          .map((el) => ({ label: el || "(Blank)", value: el })),
        status: action.payload.result
          .map((el) => el.status)
          .filter((el, index, arr) => arr.indexOf(el) === index)
          .map((el) => ({ label: el || "(Blank)", value: el })),
        serialNumber: action.payload.result
          .map((el) => el.serialNumber)
          .filter((el, index, arr) => arr.indexOf(el) === index)
          .map((el) => ({ label: el || "(Blank)", value: el })),
        swVersion: action.payload.result
          .map((el) => el.swVersion)
          .filter((el, index, arr) => arr.indexOf(el) === index)
          .map((el) => ({ label: el || "(Blank)", value: el })),
      };

      const newState = produce(state, (draft) => {
        draft[action.payload.deviceId] = {
          ...draft[action.payload.deviceId],
          fetchDeviceComponentListStatus: SystemConstants.SUCCESS_STATUS,
          deviceComponentList: action.payload.result,
          filterOptions,
          selectedFilterVals: { ...filterOptions },
        };
      });
      return assign({}, state, newState);
    }
    case SystemConstants.DEVICE_COMPONENT_LIST_FETCH_ERROR: {
      const newState = produce(state, (draft) => {
        draft[action.payload] = {
          ...draft[action.payload],
          fetchDeviceComponentListStatus: SystemConstants.ERROR_STATUS,
          fetchDeviceComponentListErrorMessage:
            SystemConstants.GENERIC_ERROR_MESSAGE,
        };
      });
      return assign({}, state, newState);
    }
    case SystemConstants.CHANGE_SELECTED_FILTER_VALS: {
      const newState = produce(state, (draft) => {
        const deviceState = draft[action.payload.deviceId];
        const updatedFilterVals = {
          ...deviceState.selectedFilterVals,
          ...action.payload.selectedFilterVals,
        };

        // Check if the currently selected component row is still visible
        // after the filter change
        const selectedComp = deviceState.selectedComponent;
        let selectedComponentStillVisible = false;
        if (selectedComp) {
          selectedComponentStillVisible =
            deviceState.deviceComponentList.some(
              (el) =>
                el.componentPath === selectedComp.componentPath &&
                updatedFilterVals.component.some(
                  (f) => f.value === el.component
                ) &&
                updatedFilterVals.status.some(
                  (f) => f.value === el.status
                ) &&
                updatedFilterVals.serialNumber.some(
                  (f) => f.value === el.serialNumber
                ) &&
                updatedFilterVals.swVersion.some(
                  (f) => f.value === el.swVersion
                )
            );
        }

        draft[action.payload.deviceId] = {
          ...deviceState,
          selectedFilterVals: updatedFilterVals,
          // If selected component is no longer visible, clear the graph and metric
          ...(selectedComp && !selectedComponentStillVisible
            ? {
                componentMetrics: [],
                selectedComponentMetric: [],
                measurementVals: [],
              }
            : {}),
        };
      });
      return assign({}, state, newState);
    }
    case SystemConstants.CHANGE_SELECTED_COMPONENT: {
      const dateUtcNow = moment.utc();
      const newState = produce(state, (draft) => {
        draft[action.payload.deviceId] = {
          ...draft[action.payload.deviceId],
          selectedComponent: action.payload.selectedComponent,
          fetchComponentMetricsStatus: SystemConstants.SUCCESS_STATUS,
          fetchComponentMetricsErrorMessage: "",
          componentMetrics: [],
          selectedComponentMetric: [],
          selectedDateRange: [
            moment(dateUtcNow).subtract(1, "days").format(DATE_TIME_FORMAT),
            moment(dateUtcNow).format(DATE_TIME_FORMAT),
          ],
          fetchMeasurementValsStatus: SystemConstants.SUCCESS_STATUS,
          fetchMeasurementValsStatusErrorMessage: "",
          measurementVals: [],
        };
      });
      return assign({}, state, newState);
    }
    case SystemConstants.COMPONENT_METRICS_FETCH_REQUEST: {
      const newState = produce(state, (draft) => {
        draft[action.payload] = {
          ...draft[action.payload],
          fetchComponentMetricsStatus: SystemConstants.FETCHING_STATUS,
          fetchComponentMetricsErrorMessage: "",
          componentMetrics: [],
        };
      });
      return assign({}, state, newState);
    }
    case SystemConstants.COMPONENT_METRICS_FETCH_SUCCESS: {
      const newState = produce(state, (draft) => {
        draft[action.payload.deviceId] = {
          ...draft[action.payload.deviceId],
          fetchComponentMetricsStatus: SystemConstants.SUCCESS_STATUS,
          componentMetrics: [...action.payload.result],
        };
      });
      return assign({}, state, newState);
    }
    case SystemConstants.COMPONENT_METRICS_FETCH_ERROR: {
      const newState = produce(state, (draft) => {
        draft[action.payload] = {
          ...draft[action.payload],
          fetchComponentMetricsStatus: SystemConstants.ERROR_STATUS,
          fetchComponentMetricsErrorMessage:
            SystemConstants.GENERIC_ERROR_MESSAGE,
        };
      });
      return assign({}, state, newState);
    }
    case SystemConstants.CHANGE_SELECTED_COMPONENT_METRIC: {
      const newState = produce(state, (draft) => {
        draft[action.payload.deviceId] = {
          ...draft[action.payload.deviceId],
          selectedComponentMetric: [action.payload.componentMetric],
        };
      });
      return assign({}, state, newState);
    }
    case SystemConstants.CHANGE_SELECTED_DATE_RANGE: {
      const newState = produce(state, (draft) => {
        draft[action.payload.deviceId] = {
          ...draft[action.payload.deviceId],
          selectedDateRange: action.payload.dateRange,
        };
      });
      return assign({}, state, newState);
    }
    case SystemConstants.COMPONENT_MEASUREMENT_VALS_FETCH_REQUEST: {
      const newState = produce(state, (draft) => {
        draft[action.payload] = {
          ...draft[action.payload],
          fetchMeasurementValsStatus: SystemConstants.FETCHING_STATUS,
          fetchMeasurementValsStatusErrorMessage: "",
          measurementVals: [],
        };
      });
      return assign({}, state, newState);
    }
    case SystemConstants.COMPONENT_MEASUREMENT_VALS_FETCH_SUCCESS: {
      const newState = produce(state, (draft) => {
        draft[action.payload.deviceId] = {
          ...draft[action.payload.deviceId],
          fetchMeasurementValsStatus: SystemConstants.SUCCESS_STATUS,
          measurementVals: [...action.payload.result],
        };
      });
      return assign({}, state, newState);
    }
    case SystemConstants.COMPONENT_MEASUREMENT_VALS_FETCH_ERROR: {
      const newState = produce(state, (draft) => {
        draft[action.payload] = {
          ...draft[action.payload],
          fetchMeasurementValsStatus: SystemConstants.ERROR_STATUS,
          fetchMeasurementValsStatusErrorMessage:
            SystemConstants.GENERIC_ERROR_MESSAGE,
        };
      });
      return assign({}, state, newState);
    }
    default:
      return state;
  }
}
